import { NgxsSimpleChange } from '@ngxs/store'

// True when a state change altered something worth writing to storage.
//
// Every slice persists itself from ngxsOnChanges by stringifying the whole
// model and handing it to Ionic Storage. That fires on every change, and a
// Refresh produces two of them: patchState({is_refreshing: true}) on the way
// in, then Set/Merge with the rows on the way out. The first carries no new
// rows -- only the spinner flag -- so it rewrites the entire slice for nothing.
// On units that is a 3.6 MB serialise and IndexedDB write per refresh, thrown
// away immediately by the second one.
//
// The stored flag is not lost by skipping the write: every slice's Init action
// overwrites the persisted is_refreshing with the live value when it rehydrates
// from storage, so what was written there was never read back.
//
// pending_count is skipped the same way. It is only a trigger: each refresh
// bumps it on eight slices to start their uploads, which wrote every one of
// them out again, photos included. The upload watchers compare it on the live
// change, not from storage, and rehydrating bumps it again anyway.
//
// last_update too: a refresh that finds the forms unchanged still moves their
// "last updated" time, and that alone shouldn't rewrite every form and photo.
// Any refresh that does change rows saves the slice, time included.
//
// Comparison is by reference per field, which is exact here because ngxs state
// is immutable -- a slice whose rows actually changed arrives with a new array.
// A new array holding the very same rows counts as unchanged; see sameRows.
export function stateContentChanged(change: NgxsSimpleChange): boolean {
  const previous = change?.previousValue
  const current = change?.currentValue
  // First change of the session, or a shape we don't recognise: persist it.
  if (!previous || !current) return true

  const keys = new Set([...Object.keys(previous), ...Object.keys(current)])
  keys.delete('is_refreshing')
  keys.delete('pending_count')
  keys.delete('last_update')
  for (const key of keys) {
    if (previous[key] !== current[key] && !sameElements(previous[key], current[key])) return true
  }
  return false
}

function sameElements(a: any, b: any): boolean {
  return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((row, i) => row === b[i])
}

// The rows a slice already holds, in a new array, for a refresh that brought
// nothing new: the server said the list is unchanged, or the request failed.
//
// A page's refresh clears its own copy of the lists it shows (resetModelData)
// and waits for each slice to emit again. Leaving the same array in place emits
// nothing, so the page stayed empty, or behind its busy overlay, for good. A new
// array of the same rows makes the selectors emit, and stateContentChanged sees
// the same rows and skips the storage write.
export function sameRows<T>(rows: T[] | undefined | null): T[] {
  return [...(rows || [])]
}
